// @ts-check
// =============================================================================
// js/net/http.js — the two HTTP endpoints: POST /login and POST /request.
//
// WHAT THIS IS
//   The only place the client talks HTTP to the game server (old client:
//   LoginToServer :328 and RequestData :4440). Screens NEVER call fetch()
//   themselves — they call login() / request() so auth handling, the
//   "unverified -> re-auth" flow, and error reporting live in one spot.
//
// THE CONTRACT (must match src/auth/login.js + src/api/request-router.js)
//   POST /login    form-encoded  username=<reference|JustAGuest…>&guest=<code>
//                  -> the full userData JSON (or {unverified:true})
//   POST /request  JSON {request, data, user, nickname, id, guest, reference,
//                  token} -> handler-specific JSON (or {unverified:true})
//   For GUESTS, /request reads the user's data from the request body itself
//   (see request-router.js "userData = req.body"), so we merge the guest's
//   whole userData into the payload exactly like the old client did.
// =============================================================================

import { store } from '../core/store.js';

/** Called when the server says our session is dead (set by session.js). */
let onUnverified = () => {};

/**
 * Register the "session invalid" handler (session.js supplies the OAuth
 * re-auth redirect). Avoids a circular import between http and session.
 * @param {() => void} handler
 * @returns {void}
 */
export function setUnverifiedHandler(handler) {
	onUnverified = handler;
}

/**
 * POST /login.
 * @param {string} entry      The reference ("<id>F<token>") or "JustAGuest<ts>".
 * @param {string} guestCode  The ?guest= invite code, or "none".
 * @param {object|null} [settings]  GUESTS ONLY: their stored content/gender
 *   filters. The server has no DB row for a guest, so the client replays them
 *   at login and the server derives the art collection from them.
 * @param {string|null} [nickname]  GUESTS ONLY: their stored nickname, replayed
 *   so the server reuses it instead of minting a new random name each load.
 * @returns {Promise<any>}    The parsed login payload (may be {unverified:true}).
 * @throws  On network failure or non-2xx response.
 */
export async function login(entry, guestCode = 'none', settings = null, nickname = null) {
	let body = `username=${encodeURIComponent(entry)}&guest=${encodeURIComponent(guestCode)}`;
	if (settings) {
		body += `&settings=${encodeURIComponent(JSON.stringify(settings))}`;
	}
	if (nickname) {
		body += `&nickname=${encodeURIComponent(nickname)}`;
	}
	const response = await fetch('/login', {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body,
	});
	if (!response.ok) {
		throw new Error(`login failed: HTTP ${response.status}`);
	}
	return response.json();
}

/**
 * POST /request — the multiplexed action endpoint (Save Settings, Save Deck,
 * Collect Pack, Host/Join/Spectate/Load/Kill/Bot Game, Lobby).
 *
 * @param {string} name   The request name, e.g. "Save Deck".
 * @param {object} [data] Handler-specific payload.
 * @returns {Promise<any|undefined>}  Parsed JSON, or undefined on failure
 *   (an "unverified" response triggers the re-auth flow and returns undefined).
 */
export async function request(name, data = {}) {
	const userData = store.userData;
	if (!userData) {
		console.error(`request("${name}") before login — dropped`);
		return undefined;
	}

	// Base payload: what the server's request-router expects for auth/dispatch.
	const payload = {
		request: name,
		data,
		user: userData.name,
		nickname: userData.nickname,
		id: userData.id,
		guest: userData.guest,
		reference: userData.reference,
		token: userData.token,
	};

	// Guests are stateless server-side: the router reads their account straight
	// from the request body, so a guest ships its whole userData along (the old
	// client did this implicitly by posting userData-shaped fields).
	const body = store.session.isGuest ? { ...userData, ...payload } : payload;

	try {
		const response = await fetch('/request', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
		if (!response.ok) {
			console.error(`request("${name}") -> HTTP ${response.status}`);
			return undefined;
		}
		const json = await response.json();
		if (json && json.unverified) {
			// Session token no longer valid: hand off to the re-auth flow.
			onUnverified();
			return undefined;
		}
		return json;
	} catch (error) {
		console.error(`request("${name}") network error:`, error);
		return undefined;
	}
}
